import { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { AriaLive } from '@pretamanger/component-library'
import Translation, { translate } from '#src/common/components/translation'
import { BodyLock } from '#src/common/components/body-lock'
import { localisePrice } from '#src/common/lib/locale'
import { OrderAheadBagContext } from '#src/common/context/order-ahead-bag'
import BagItems from './bag-items'

import {
  BagCross,
  BagOverlay,
  BagWrapper,
  CloseButtonWrapper,
  LoadingText,
  BagFooter,
  CheckoutButton,
  BagPanel,
  BagContent,
  BagPriceWrapper,
  BagTotalPrice,
  Heading,
  HeadingWrapper,
  StyledButton
} from './styles'

import { LastOrderIcon } from '../styles'
import { useFocusTrap } from '#hooks'
import { actionField } from '#src/common/lib/events/topics'

const LastOrderBag = ({
  isOpen,
  lastOrderItems,
  onCloseBag,
  onLastOrderAddedToBag
}) => {
  const { locale } = useRouter()
  const { addItemsToBag } = useContext(OrderAheadBagContext)

  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false)
  const [currentItems, setCurrentItems] = useState([])
  const [totalSummary, setTotalSummary] = useState()
  const [isAllUnavailable, setIsAllUnavailable] = useState(false)
  const bagRef = useFocusTrap()

  const resetLastOrderItems = () => {
    setCurrentItems(JSON.parse(JSON.stringify(lastOrderItems)))
  }

  const calculateTotalSummary = () => {
    const availableItems = currentItems.filter(
      item => item.availability.available
    )
    setTotalSummary({
      quantity: availableItems.reduce(
        (a, b) => a + b.recalculatedLineItem.quantity,
        0
      ),
      totalPrice: {
        currencyCode:
          availableItems[0]?.recalculatedLineItem.totalPrice.currencyCode,
        fractionDigits:
          availableItems[0]?.recalculatedLineItem.totalPrice.fractionDigits,
        centAmount: availableItems.reduce(
          (a, b) => a + b.recalculatedLineItem.totalPrice.centAmount,
          0
        )
      }
    })
  }

  const onItemQuantityUpdated = (item, newQuantity) => {
    setCurrentItems(currentItems => {
      let clonedCurrentItems = JSON.parse(JSON.stringify(currentItems))
      if (newQuantity > 0) {
        const itemToUpdate = clonedCurrentItems.find(
          i => i.recalculatedLineItem.id === item.recalculatedLineItem.id
        )
        itemToUpdate.recalculatedLineItem.quantity = newQuantity
        itemToUpdate.recalculatedLineItem.totalPrice.centAmount =
          itemToUpdate.recalculatedLineItem.price.centAmount * newQuantity
        itemToUpdate.recalculatedLineItem.totalPrice.localisedPrice =
          localisePrice(itemToUpdate.recalculatedLineItem.totalPrice, locale)
      } else {
        clonedCurrentItems = clonedCurrentItems.filter(
          i => i.recalculatedLineItem.id !== item.recalculatedLineItem.id
        )
      }
      return clonedCurrentItems
    })
    calculateTotalSummary()
  }

  useEffect(() => {
    setIsAllUnavailable(
      lastOrderItems.every(item => !item.availability.available)
    )
    resetLastOrderItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastOrderItems])

  useEffect(() => {
    calculateTotalSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItems])

  useEffect(() => {
    if (isOpen) {
      bagRef.current.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onAddToBag = async () => {
    setShowLoadingIndicator(true)
    const products = currentItems
      .filter(item => item.availability.available)
      .map(item => item.recalculatedLineItem)
    await addItemsToBag(products, actionField.orderAgain)
    setShowLoadingIndicator(false)
    onLastOrderAddedToBag()
  }

  const onCloseButtonClick = () => {
    resetLastOrderItems()
    onCloseBag()
  }

  return (
    <>
      {isOpen && (
        <BodyLock>
          <BagOverlay data-testid='bag-overlay' onClick={onCloseButtonClick} />
        </BodyLock>
      )}
      <BagWrapper
        data-testid='bag-wrapper'
        tabIndex={-1}
        ref={bagRef}
        isOpened={isOpen}
        aria-expanded={isOpen}
      >
        <BagPanel>
          {showLoadingIndicator ? (
            <LoadingText>
              <Translation id='product.add-items-to-bag.label' />
            </LoadingText>
          ) : (
            <>
              <CloseButtonWrapper>
                <StyledButton
                  data-testid='close-button'
                  title={
                    translate('products.last-order.close.button', locale)?.value
                  }
                  onClick={onCloseButtonClick}
                >
                  <BagCross colour='grey-700' />
                </StyledButton>
              </CloseButtonWrapper>
              <HeadingWrapper>
                <Heading>
                  <LastOrderIcon className='text-current' />
                  <Translation id='products.last-order.order-again' />
                </Heading>
              </HeadingWrapper>
              <BagContent>
                <BagItems
                  lineItems={currentItems}
                  onQuantityChange={onItemQuantityUpdated}
                />
              </BagContent>
              <BagFooter>
                {totalSummary && (
                  <>
                    <BagPriceWrapper>
                      <BagTotalPrice>
                        <div>
                          <span
                            className={
                              isAllUnavailable ? 'text-gray-400' : null
                            }
                            data-testid='total-price-heading'
                          >
                            {
                              translate('bag.order.totalCost', locale, {
                                count: totalSummary.quantity
                              })?.value
                            }
                          </span>
                          <span
                            className={
                              isAllUnavailable ? 'text-gray-400' : null
                            }
                          >
                            {localisePrice(totalSummary.totalPrice, locale)}
                          </span>
                        </div>
                      </BagTotalPrice>
                    </BagPriceWrapper>
                    <AriaLive role='alert'>
                      <CheckoutButton
                        styleType='primary'
                        disabled={isAllUnavailable}
                        data-testid='checkout-button'
                        onClick={onAddToBag}
                      >
                        <Translation id='product.add-to-bag.label' />
                        <span aria-hidden='true'> | </span>
                        <span>
                          {localisePrice(totalSummary.totalPrice, locale)}
                        </span>
                      </CheckoutButton>
                    </AriaLive>
                  </>
                )}
              </BagFooter>
            </>
          )}
        </BagPanel>
      </BagWrapper>
    </>
  )
}

LastOrderBag.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  lastOrderItems: PropTypes.array,
  onCloseBag: PropTypes.func.isRequired,
  onLastOrderAddedToBag: PropTypes.func.isRequired
}

LastOrderBag.defaultProps = {
  lastOrderItems: []
}

export default LastOrderBag
